import { useState, ReactElement } from 'react';
import { Controller, Control, FieldErrors, FieldValues, Path } from 'react-hook-form';
import get from 'lodash/get';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import FormHelperText from '@mui/material/FormHelperText';

import AvatarUpload from '~/ui/components/common/AvatarUpload';

import getFileTypeIconByMimeType from '~/ui/pages/Resources/helpers/getFileTypeIconByMimeType';

import AcceptableResourceMimeTypes from '~/ui/pages/Resources/models/AcceptableResourceMimeTypes';
import styles from './FileUpload.module.scss';

interface IProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  errors: FieldErrors;
  name: string;
  label?: string;
}

const FileUploadComponent = <TFieldValues extends FieldValues = FieldValues>({
  control,
  errors,
  name,
  label = '',
}: IProps<TFieldValues>): ReactElement => {
  const [uploadError, setUploadError] = useState('');
  const error = get(errors, `${name}.message`, '');

  return (
    <Controller
      control={control}
      name={name as Path<TFieldValues>}
      render={({ field: { value, onChange } }) => {
        const file = value as File | undefined;
        return (
          <FormControl component="fieldset" className={styles.uploadControl} fullWidth>
            <FormLabel component="legend" className={styles.label}>
              {label}
            </FormLabel>
            <AvatarUpload
              contentTitle="File"
              acceptTypes={Object.values(AcceptableResourceMimeTypes).join(',')}
              value={file}
              maxFileSize={20}
              onUpload={uploadedFile => {
                onChange(uploadedFile);
                setUploadError('');
              }}
              className={styles.filePreviewContainer}
              filePreview={
                file?.type ? (
                  <div className={styles.previewIconContainer}>
                    {getFileTypeIconByMimeType(file.type as AcceptableResourceMimeTypes)}
                  </div>
                ) : null
              }
              onRemove={() => onChange('')}
              onError={err => setUploadError(err)}
            />
            {uploadError && <FormHelperText error>{uploadError}</FormHelperText>}
            {error && <FormHelperText error>{error.toString()}</FormHelperText>}
          </FormControl>
        );
      }}
    />
  );
};

export default FileUploadComponent;
